<script setup lang="ts">
/**
 * PreviewLinkModal — 1:1 порт docs/mockups/modal-preview + spec
 * (modals-behavior.md `modal-preview`) (inbox-192).
 *
 * Size sm, info-only — короткий URL, target, QR mini, sparkline 7d,
 * 3 кнопки (открыть/копировать/поделиться).
 *
 * Payload: { shortUrl, targetUrl, slug, clicks7d?: number[] }
 * Emit: 'preview-link' { closed: true } on close.
 */
import { computed, ref } from 'vue';
import { useModalStore } from '@/stores/modal';
import UiModal from '@/components/ui/UiModal.vue';
import UiButton from '@/components/ui/UiButton.vue';

const modals = useModalStore();

interface Payload {
  shortUrl: string;
  targetUrl: string;
  slug: string;
  clicks7d?: number[];
}
const payload = computed<Payload>(() => {
  const raw = (modals.payload || {}) as Record<string, unknown>;
  return {
    shortUrl: typeof raw.shortUrl === 'string' ? raw.shortUrl : 'lnk.pl/example',
    targetUrl: typeof raw.targetUrl === 'string' ? raw.targetUrl : 'https://example.com',
    slug: typeof raw.slug === 'string' ? raw.slug : 'example',
    clicks7d: Array.isArray(raw.clicks7d) ? raw.clicks7d.filter((n): n is number => typeof n === 'number') : [42, 38, 51, 48, 62, 58, 67],
  };
});

const copied = ref(false);
async function copyLink() {
  try {
    await navigator.clipboard.writeText(payload.value.shortUrl);
    copied.value = true;
    setTimeout(() => (copied.value = false), 1500);
  } catch { /* fail-quiet */ }
}

function openExternal() {
  window.open(payload.value.shortUrl, '_blank', 'noopener');
}

async function shareLink() {
  const nav = navigator as Navigator & { share?: (data: ShareData) => Promise<void> };
  if (nav.share) {
    try { await nav.share({ url: payload.value.shortUrl, title: 'Короткая ссылка' }); } catch { /* fail-quiet */ }
  } else { copyLink(); }
}

const sparkPath = computed(() => {
  const data = payload.value.clicks7d || [];
  if (data.length === 0) return '';
  const max = Math.max(...data, 1);
  const step = 100 / Math.max(data.length - 1, 1);
  return data.map((v, i) => `${i === 0 ? 'M' : 'L'} ${i * step} ${30 - (v / max) * 28}`).join(' ');
});
</script>

<template>
  <UiModal title="Предпросмотр ссылки" size="sm" @close="modals.close">
    <div class="preview-body">
      <div class="qr-block">
        <div class="qr-mock" aria-label="QR-код">
          <!-- Mock QR pattern (real QR svg generates server-side) -->
          <svg viewBox="0 0 80 80" width="100" height="100">
            <rect x="0" y="0" width="80" height="80" fill="#fff"/>
            <g fill="#0a0d12">
              <rect x="4" y="4" width="20" height="20"/><rect x="8" y="8" width="12" height="12" fill="#fff"/><rect x="12" y="12" width="4" height="4"/>
              <rect x="56" y="4" width="20" height="20"/><rect x="60" y="8" width="12" height="12" fill="#fff"/><rect x="64" y="12" width="4" height="4"/>
              <rect x="4" y="56" width="20" height="20"/><rect x="8" y="60" width="12" height="12" fill="#fff"/><rect x="12" y="64" width="4" height="4"/>
              <rect x="30" y="30" width="4" height="4"/><rect x="38" y="30" width="4" height="4"/><rect x="46" y="30" width="4" height="4"/>
              <rect x="30" y="38" width="4" height="4"/><rect x="46" y="38" width="4" height="4"/>
              <rect x="34" y="46" width="4" height="4"/><rect x="42" y="46" width="4" height="4"/>
              <rect x="30" y="54" width="4" height="4"/><rect x="42" y="54" width="4" height="4"/>
            </g>
          </svg>
        </div>
        <div class="links-block">
          <div class="link-row">
            <div class="link-label">Короткая ссылка</div>
            <code class="link-short">{{ payload.shortUrl }}</code>
          </div>
          <div class="link-row">
            <div class="link-label">Ведёт на</div>
            <a :href="payload.targetUrl" target="_blank" rel="noopener" class="link-target">{{ payload.targetUrl }}</a>
          </div>
        </div>
      </div>

      <div class="spark-block">
        <div class="spark-label">Клики за 7 дней</div>
        <svg class="spark" viewBox="0 0 100 30" preserveAspectRatio="none">
          <path :d="sparkPath" fill="none" stroke="#4f8cff" stroke-width="1.5" />
        </svg>
        <div class="spark-foot">
          <span>{{ payload.clicks7d?.reduce((a, b) => a + b, 0) || 0 }} кликов всего</span>
          <span>пик {{ Math.max(...(payload.clicks7d || [0])) }}</span>
        </div>
      </div>
    </div>

    <template #footer>
      <UiButton variant="outline" @click="openExternal">Открыть в новой вкладке</UiButton>
      <UiButton variant="outline" @click="copyLink">{{ copied ? 'Скопировано ✓' : 'Скопировать' }}</UiButton>
      <UiButton variant="primary" @click="shareLink">Поделиться</UiButton>
    </template>
  </UiModal>
</template>

<style scoped>
.preview-body { display: flex; flex-direction: column; gap: 20px; }
.qr-block { display: flex; gap: 16px; align-items: flex-start; }
.qr-mock { flex-shrink: 0; padding: 6px; background: #fff; border-radius: 8px; }
.links-block { flex: 1; display: flex; flex-direction: column; gap: 12px; min-width: 0; }
.link-row { display: flex; flex-direction: column; gap: 4px; }
.link-label { font-size: 11px; color: var(--fg-dim, #6b7480); text-transform: uppercase; letter-spacing: 0.04em; font-weight: 600; }
.link-short {
  font-family: var(--font-mono, ui-monospace, monospace); font-size: 14px;
  color: var(--accent-hi, #6f9eff);
  background: var(--bg, #0f1620); padding: 6px 10px; border-radius: 6px;
}
.link-target {
  color: var(--fg, #e6eaf0); font-size: 13px; text-decoration: none;
  word-break: break-all;
}
.link-target:hover { color: var(--accent-hi, #6f9eff); text-decoration: underline; }

.spark-block { background: var(--bg-card, #1a1f29); border: 1px solid var(--border, #2a3340); border-radius: 8px; padding: 12px; }
.spark-label { font-size: 11px; color: var(--fg-dim, #6b7480); text-transform: uppercase; letter-spacing: 0.04em; font-weight: 600; margin-bottom: 6px; }
.spark { width: 100%; height: 40px; }
.spark-foot { display: flex; justify-content: space-between; font-size: 11.5px; color: var(--fg-muted, #9aa5b1); margin-top: 4px; }
</style>
